import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { Save, Loader2, Plus, AlertTriangle, Search, Check } from 'lucide-react';
import { ticketService } from '../../services/api';
import { WC_MATCHES } from '../../data/worldCupSchedule';
import { getRGSchedule } from '../../data/rgSchedule';
import { RUGBY_MATCHES } from '../../data/rugbySchedule';

// Sources prédéfinies courantes
const PREDEFINED_SOURCES = ['VG', 'MAXES', 'TMB01', 'REACH01', 'TICKETMASTER', 'VIAGOGO', 'FTN', 'STUBHUB'];

// Catégories prédéfinies
const PREDEFINED_CATEGORIES = ['CATEGORY 1', 'CATEGORY 2', 'CATEGORY 3', 'CATEGORY 4', 'CATEGORIE 1', 'CATEGORIE 2', 'CATEGORIE 3', 'CATEGORIE 4', 'VIP', 'HOSPITALITY', 'UPPER', 'LOWER'];

export const TicketFormModal = ({ open, onOpenChange, ticket, onSave, allTickets = [], project = '', addedSources = [], setAddedSources }) => {
  const isWC = project && project.toUpperCase().startsWith('WORLD CUP');
  const isRG = project && (project.toUpperCase().includes('ROLAN') || project.toUpperCase().includes('RG'));
  const isRugby = project && project.toUpperCase().includes('RUGBY');
  
  // Build event list for dropdown
  const eventList = useMemo(() => {
    if (isRugby) {
      return RUGBY_MATCHES.map(m => ({
        label: `M${m.matchNum} - ${m.teams} - ${m.stage} - ${m.venue} ${m.city} - ${m.date} ${m.time}`,
        evenement: `M${m.matchNum} -( ${m.stage} ) - ${m.teams}`,
        date_evenement: m.date,
        heure_evenement: m.time,
        lieu: m.venue,
        ville: m.city,
        pays: 'Australie',
      }));
    }
    if (isWC) {
      return WC_MATCHES.map(m => ({
        label: `M${m.matchNum} - ${m.teams} - ${m.stage} - ${m.venue} ${m.city} - ${m.date} ${m.time}`,
        evenement: `M${m.matchNum} -( ${m.stage.toUpperCase()} ) - ${m.teams}`,
        date_evenement: m.date,
        heure_evenement: m.time,
        lieu: m.venue,
        ville: m.city,
        pays: m.city?.includes('Mexico') || m.city?.includes('Guadalajara') || m.city?.includes('Monterrey') ? 'Mexique' 
              : m.city?.includes('Toronto') || m.city?.includes('Vancouver') ? 'Canada' : 'États-Unis',
      }));
    }
    if (isRG) {
      return getRGSchedule(project).map(s => {
        const courtShort = s.court?.includes('Chatrier') ? 'Chatrier' : s.court?.includes('Lenglen') ? 'Lenglen' : s.court?.includes('Mathieu') ? 'Mathieu' : 'Outside';
        const sessionStr = s.session ? ` - ${s.session}` : '';
        return {
          label: `Day ${s.day} - ${s.phase}${sessionStr} - ${courtShort} - ${s.date}`,
          evenement: `M${s.day} - Day ${s.day} - ${s.phase}${sessionStr} - ${courtShort}`,
          date_evenement: s.date,
          heure_evenement: '',
          lieu: s.court,
          ville: 'Paris',
          pays: 'France',
          session: s.session || '',
        };
      });
    }
    return [];
  }, [isWC, isRG, isRugby, project]);
  
  // Event search state
  const [eventSearch, setEventSearch] = useState('');
  const [showEventDropdown, setShowEventDropdown] = useState(false);
  const eventDropdownRef = useRef(null);
  
  const filteredEvents = useMemo(() => {
    if (!eventSearch.trim()) return eventList.slice(0, 20);
    // Recherche tolérante : "SEMI FINAL 1" matche "Men's Semifinal #1"
    const compact = (s) => s.toLowerCase().replace(/[\s\-'’#()]/g, '');
    const search = eventSearch.toLowerCase();
    const searchC = compact(eventSearch);
    // Chaque mot/terme doit apparaître (version normale OU compacte)
    const terms = search.split(/\s+/).filter(Boolean);
    return eventList.filter(e => {
      const label = e.label.toLowerCase();
      const labelC = compact(e.label);
      if (label.includes(search) || labelC.includes(searchC)) return true;
      return terms.every(t => label.includes(t) || labelC.includes(compact(t)));
    }).slice(0, 20);
  }, [eventList, eventSearch]);
  
  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      if (eventDropdownRef.current && !eventDropdownRef.current.contains(e.target)) {
        setShowEventDropdown(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);
  
  const selectEvent = (event) => {
    // Convert DD/MM/YYYY to YYYY-MM-DD for HTML date input
    let dateForInput = event.date_evenement || '';
    if (dateForInput.includes('/')) {
      const parts = dateForInput.split('/');
      if (parts.length === 3) dateForInput = `${parts[2]}-${parts[1]}-${parts[0]}`;
    }
    setFormData(prev => ({
      ...prev,
      evenement: event.evenement,
      date_evenement: dateForInput,
      heure_evenement: event.heure_evenement || prev.heure_evenement,
      lieu: event.lieu,
      ville: event.ville,
      pays: event.pays,
      ...(event.session !== undefined ? { session: event.session } : {}),
    }));
    setEventSearch('');
    setShowEventDropdown(false);
  };
  
  // Auto-generate reference
  const [genLoading, setGenLoading] = useState(null);
  const generateRef = async (refType) => {
    const source = formData.source?.trim();
    if (!source) {
      alert('Renseignez la SOURCE d\'abord');
      return;
    }
    setGenLoading(refType);
    try {
      const res = await fetch(`/api/tickets/next-ref?source=${encodeURIComponent(source)}&project=${encodeURIComponent(project)}&ref_type=${refType}`);
      const data = await res.json();
      if (data.ref) {
        handleChange(refType === 'achat' ? 'ref_achat' : 'ref_vente', data.ref);
      }
    } catch (err) {
      console.error('Erreur génération ref:', err);
    } finally {
      setGenLoading(null);
    }
  };
  
  // Helper: get today's date as DD/MM/YYYY
  const getTodayDate = () => {
    const d = new Date();
    return `${String(d.getDate()).padStart(2,'0')}/${String(d.getMonth()+1).padStart(2,'0')}/${d.getFullYear()}`;
  };
  
  const [formData, setFormData] = useState({
    type: 'ACHAT',
    ref_achat: '',
    ref_vente: '',
    statut: 'PENDING',
    evenement: '',
    date_evenement: '',
    heure_evenement: '',
    lieu: '',
    ville: '',
    pays: '',
    categorie: '',
    block: '',
    rang: '',
    place: '',
    tribune: '',
    acces: '',
    escalier: '',
    qty: 1,
    prix: 0,
    devise: 'EUR',
    total: 0,
    mode_livraison: '',
    nom_client: '',
    mail_client: '',
    dob_client: '',
    source: '',
    acheteur_original: '',
    note: '',
    email_compte: '',
    pw_compte: '',
    date_operation: '',
    place_fin: '',
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (ticket) {
      setFormData({
        type: ticket.type || 'ACHAT',
        ref_achat: ticket.ref_achat || '',
        ref_vente: ticket.ref_vente || '',
        statut: ticket.statut || 'PENDING',
        evenement: ticket.evenement || '',
        date_evenement: ticket.date_evenement || '',
        heure_evenement: ticket.heure_evenement || '',
        lieu: ticket.lieu || '',
        ville: ticket.ville || '',
        pays: ticket.pays || '',
        categorie: ticket.categorie || '',
        block: ticket.block || '',
        rang: ticket.rang || '',
        place: ticket.place || '',
        tribune: ticket.tribune || '',
        acces: ticket.acces || '',
        escalier: ticket.escalier || '',
        qty: ticket.qty || 1,
        prix: ticket.prix || 0,
        devise: ticket.devise || 'EUR',
        total: ticket.total || 0,
        mode_livraison: ticket.mode_livraison || '',
        nom_client: ticket.nom_client || '',
        mail_client: ticket.mail_client || '',
        dob_client: ticket.dob_client || '',
        source: ticket.source || '',
        acheteur_original: ticket.acheteur_original || '',
        note: ticket.note || '',
        email_compte: ticket.email_compte || '',
        pw_compte: ticket.pw_compte || '',
        date_operation: ticket.date_operation || '',
        place_fin: ticket.place_fin || '',
      });
    } else {
      setFormData({
        type: 'ACHAT',
        ref_achat: '',
        ref_vente: '',
        statut: 'PENDING',
        evenement: '',
        date_evenement: '',
        heure_evenement: '',
        lieu: '',
        ville: '',
        pays: '',
        categorie: '',
        block: '',
        rang: '',
        place: '',
        tribune: '',
        acces: '',
        escalier: '',
        qty: 1,
        prix: 0,
        devise: 'EUR',
        total: 0,
        mode_livraison: '',
        nom_client: '',
        mail_client: '',
        dob_client: '',
        source: '',
        acheteur_original: '',
        note: '',
        email_compte: '',
        pw_compte: '',
        date_operation: getTodayDate(),
      });
    }
  }, [ticket, open]);

  // Extraire toutes les sources uniques des tickets existants
  const availableSources = useMemo(() => {
    const sources = new Set(PREDEFINED_SOURCES);
    allTickets.forEach(t => {
      if (t.source && t.source.trim()) {
        sources.add(t.source.trim().toUpperCase());
      }
    });
    addedSources.forEach(s => sources.add(s));
    return Array.from(sources).sort();
  }, [allTickets, addedSources]);

  // Extraire toutes les catégories uniques des tickets existants
  const availableCategories = useMemo(() => {
    const categories = new Set(PREDEFINED_CATEGORIES);
    allTickets.forEach(t => {
      if (t.categorie && t.categorie.trim()) {
        categories.add(t.categorie.trim().toUpperCase());
      }
    });
    return Array.from(categories).sort();
  }, [allTickets]);

  // État pour nouvelle source/catégorie personnalisée
  const [showCustomSource, setShowCustomSource] = useState(false);
  const [showCustomCategory, setShowCustomCategory] = useState(false);
  const [customSource, setCustomSource] = useState('');
  const [customCategory, setCustomCategory] = useState('');
  const [duplicateWarning, setDuplicateWarning] = useState(null);
  const [previewLines, setPreviewLines] = useState(null);

  // Check for duplicate when ref changes (only for new tickets)
  const checkDuplicate = async (type, ref) => {
    if (ticket || !ref || ref.trim() === '') {
      setDuplicateWarning(null);
      return;
    }
    try {
      const result = await ticketService.checkDuplicates(
        [{ type, ref_achat: type === 'ACHAT' ? ref : '', ref_vente: type === 'VENTE' ? ref : '' }],
        project
      );
      if (result.duplicates?.length > 0) {
        setDuplicateWarning(result.duplicates[0]);
      } else {
        setDuplicateWarning(null);
      }
    } catch {
      setDuplicateWarning(null);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => {
      const newData = { ...prev, [field]: value };
      
      // Quand le type change, la date_operation reste (toujours date du jour)
      
      // Auto-calculate total
      if (field === 'qty' || field === 'prix') {
        const qty = field === 'qty' ? value : prev.qty;
        const prix = field === 'prix' ? value : prev.prix;
        newData.total = (parseFloat(qty) || 0) * (parseFloat(prix) || 0);
      }
      
      return newData;
    });
  };

  // Normalize date helper
  const normalizeDate = (data) => {
    const norm = { ...data };
    if (norm.date_evenement) {
      const iso = norm.date_evenement.match(/^(\d{4})-(\d{2})-(\d{2})/);
      if (iso) norm.date_evenement = `${iso[3]}/${iso[2]}/${iso[1]}`;
    }
    return norm;
  };

  // Build preview lines for ACHAT split
  const buildPreviewLines = (data) => {
    const qty = parseInt(data.qty) || 1;
    const isAchat = data.type === 'ACHAT';
    if (isAchat && qty > 1) {
      const placeDebut = parseInt(data.place) || 0;
      return Array.from({ length: qty }, (_, i) => ({
        ...data,
        qty: 1,
        place: placeDebut ? String(placeDebut + i) : '',
        place_fin: placeDebut ? String(placeDebut + i) : '',
        _lineNum: i + 1,
      }));
    }
    return [{ ...data, _lineNum: 1 }];
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.categorie || formData.categorie.trim() === '') {
      alert('Il manque la catégorie, merci de la rentrer avant de valider.');
      return;
    }
    
    if (duplicateWarning && !duplicateWarning.confirmed) {
      setDuplicateWarning({ ...duplicateWarning, showConfirm: true });
      return;
    }
    
    // For editing existing ticket, save directly + rename refs if changed
    if (ticket?.id) {
      setLoading(true);
      try {
        const normalizedData = normalizeDate(formData);
        
        // Check if ref_achat or ref_vente changed → rename across all tickets in project
        const oldRefAchat = (ticket.ref_achat || '').trim();
        const newRefAchat = (normalizedData.ref_achat || '').trim();
        const oldRefVente = (ticket.ref_vente || '').trim();
        const newRefVente = (normalizedData.ref_vente || '').trim();

        // Detect if the OLD ref is SHARED by multiple VENTE (or ACHAT) tickets.
        // When shared, we must NOT rename in cascade (would rename siblings too).
        // Only rename in cascade when this ticket is the unique owner of the ref.
        const countVenteSharingRef = (ref) => allTickets.filter(t => t.type === 'VENTE' && (t.ref_vente || '').trim() === ref).length;
        const countAchatSharingRef = (ref) => allTickets.filter(t => t.type === 'ACHAT' && (t.ref_achat || '').trim() === ref).length;

        if (oldRefAchat && newRefAchat && oldRefAchat !== newRefAchat) {
          const sharedAchat = countAchatSharingRef(oldRefAchat) > 1;
          if (!sharedAchat) {
            try {
              await ticketService.renameRef(oldRefAchat, newRefAchat, 'ref_achat', project);
            } catch (err) { console.error('Rename ref_achat failed:', err); }
          }
        }
        if (oldRefVente && newRefVente && oldRefVente !== newRefVente) {
          const sharedVente = countVenteSharingRef(oldRefVente) > 1;
          if (!sharedVente) {
            try {
              await ticketService.renameRef(oldRefVente, newRefVente, 'ref_vente', project);
            } catch (err) { console.error('Rename ref_vente failed:', err); }
          }
        }
        
        await onSave(normalizedData, ticket.id);
        onOpenChange(false);
      } finally {
        setLoading(false);
      }
      return;
    }
    
    // For new ticket: show preview
    const normalized = normalizeDate(formData);
    const lines = buildPreviewLines(normalized);
    setPreviewLines(lines);
  };
  
  const handleConfirmPreview = async () => {
    if (!previewLines) return;
    setLoading(true);
    try {
      for (const line of previewLines) {
        const { _lineNum, ...data } = line;
        await onSave(data, null);
      }
      setPreviewLines(null);
      onOpenChange(false);
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmDuplicate = async () => {
    setDuplicateWarning({ ...duplicateWarning, confirmed: true, showConfirm: false });
    // For editing existing ticket, save directly + rename refs if changed
    if (ticket?.id) {
      setLoading(true);
      try {
        const normalizedData = normalizeDate(formData);
        
        const oldRefAchat = (ticket.ref_achat || '').trim();
        const newRefAchat = (normalizedData.ref_achat || '').trim();
        const oldRefVente = (ticket.ref_vente || '').trim();
        const newRefVente = (normalizedData.ref_vente || '').trim();

        const countVenteSharingRef = (ref) => allTickets.filter(t => t.type === 'VENTE' && (t.ref_vente || '').trim() === ref).length;
        const countAchatSharingRef = (ref) => allTickets.filter(t => t.type === 'ACHAT' && (t.ref_achat || '').trim() === ref).length;

        if (oldRefAchat && newRefAchat && oldRefAchat !== newRefAchat) {
          const sharedAchat = countAchatSharingRef(oldRefAchat) > 1;
          if (!sharedAchat) {
            try {
              await ticketService.renameRef(oldRefAchat, newRefAchat, 'ref_achat', project);
            } catch (err) { console.error('Rename ref_achat failed:', err); }
          }
        }
        if (oldRefVente && newRefVente && oldRefVente !== newRefVente) {
          const sharedVente = countVenteSharingRef(oldRefVente) > 1;
          if (!sharedVente) {
            try {
              await ticketService.renameRef(oldRefVente, newRefVente, 'ref_vente', project);
            } catch (err) { console.error('Rename ref_vente failed:', err); }
          }
        }
        
        await onSave(normalizedData, ticket.id);
        onOpenChange(false);
      } finally {
        setLoading(false);
      }
      return;
    }
    // For new ticket: show preview
    const normalized = normalizeDate(formData);
    const lines = buildPreviewLines(normalized);
    setPreviewLines(lines);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent 
        className="sm:max-w-2xl max-h-[90vh] overflow-y-auto bg-card border-border"
        onInteractOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle className="font-heading text-xl">
            {ticket ? 'Modifier le Billet' : 'Nouveau Billet'}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6 mt-4">
          {/* Type & Status */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Type *</Label>
              <Select
                value={formData.type}
                onValueChange={(v) => handleChange('type', v)}
              >
                <SelectTrigger data-testid="ticket-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ACHAT">ACHAT</SelectItem>
                  <SelectItem value="VENTE">VENTE</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Statut</Label>
              <Select
                value={formData.statut}
                onValueChange={(v) => handleChange('statut', v)}
              >
                <SelectTrigger data-testid="ticket-status-select">
                  <SelectValue placeholder="Sélectionner..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="PENDING">En attente</SelectItem>
                  <SelectItem value="MATCH_OK">MATCH_OK</SelectItem>
                  <SelectItem value="VALIDE">VALIDE</SelectItem>
                  <SelectItem value="ANNULEE">ANNULEE</SelectItem>
                  <SelectItem value="DOUBT">DOUBT</SelectItem>
                  <SelectItem value="DOUBLON">DOUBLON</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Reference Achat / Acheté le */}
          <div className="space-y-2">
            <Label>{isWC ? 'Acheté le' : 'Réf. Achat'}</Label>
            <div className="flex gap-1">
              <Input
                data-testid="ref-achat-input"
                value={formData.ref_achat}
                onChange={(e) => handleChange('ref_achat', e.target.value)}
                onBlur={() => formData.type === 'ACHAT' && checkDuplicate('ACHAT', formData.ref_achat)}
                placeholder={isWC ? 'DD/MM/YYYY' : 'Référence achat'}
                className="bg-background border-border/50 font-mono flex-1"
              />
              {!isWC && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => generateRef('achat')}
                  disabled={genLoading === 'achat'}
                  className="border-[#C4A052]/50 text-[#C4A052] hover:bg-[#C4A052]/20 px-2"
                  title="Générer réf auto (SOURCE-N)"
                  data-testid="gen-ref-achat-btn"
                >
                  {genLoading === 'achat' ? <Loader2 className="h-4 w-4 animate-spin" /> : '🔄'}
                </Button>
              )}
            </div>
          </div>

          {/* Reference Vente only */}
          <div className="space-y-2">
            <Label className={isRG && formData.type === 'ACHAT' ? 'text-zinc-600' : ''}>Réf. Vente</Label>
            <div className="flex gap-1">
              <Input
                data-testid="ref-vente-input"
                value={formData.ref_vente}
                onChange={(e) => handleChange('ref_vente', e.target.value)}
                onBlur={() => formData.type === 'VENTE' && checkDuplicate('VENTE', formData.ref_vente)}
                className="bg-background border-border/50 font-mono flex-1"
              />
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => generateRef('vente')}
                disabled={genLoading === 'vente'}
                className="border-[#C4A052]/50 text-[#C4A052] hover:bg-[#C4A052]/20 px-2"
                title="Générer réf auto (SOURCE-N)"
                data-testid="gen-ref-vente-btn"
              >
                {genLoading === 'vente' ? <Loader2 className="h-4 w-4 animate-spin" /> : '🔄'}
              </Button>
            </div>
          </div>

          {/* Date opération - label conditionnel selon le type */}
          <div className="space-y-2">
            <Label>{formData.type === 'VENTE' ? 'Vendu le' : 'Date Achat'}</Label>
            <Input
              data-testid="date-operation-input"
              value={formData.date_operation}
              onChange={(e) => handleChange('date_operation', e.target.value)}
              placeholder="DD/MM/YYYY"
              className="bg-background border-border/50 font-mono"
            />
          </div>

          {/* Duplicate Warning */}
          {duplicateWarning && (
            <div className="bg-amber-500/10 border border-amber-500/40 p-3 rounded-lg" data-testid="duplicate-warning">
              <div className="flex items-center gap-2 text-amber-400 text-sm font-medium mb-1">
                <AlertTriangle className="h-4 w-4" />
                Doublon détecté
              </div>
              <p className="text-xs text-amber-300/80">
                La référence <strong>{duplicateWarning.ref}</strong> existe déjà pour "{duplicateWarning.existing_event}".
                Les champs non vides seront mis à jour automatiquement.
              </p>
              {duplicateWarning.showConfirm && (
                <div className="flex gap-2 mt-2">
                  <Button
                    type="button"
                    size="sm"
                    onClick={handleConfirmDuplicate}
                    className="bg-amber-500 hover:bg-amber-600 text-black text-xs h-7"
                  >
                    Confirmer la mise à jour
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() => setDuplicateWarning(null)}
                    className="text-xs h-7 border-zinc-600"
                  >
                    Annuler
                  </Button>
                </div>
              )}
            </div>
          )}

          {/* Event - Searchable dropdown */}
          <div className="space-y-2" ref={eventDropdownRef}>
            <Label>Événement *</Label>
            <div className="relative">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-400" />
                <Input
                  data-testid="evenement-input"
                  autoComplete="off"
                  name="evenement-search-noautofill"
                  value={showEventDropdown ? eventSearch : formData.evenement}
                  onChange={(e) => {
                    if (eventList.length > 0) {
                      setEventSearch(e.target.value);
                      setShowEventDropdown(true);
                    }
                    handleChange('evenement', e.target.value);
                  }}
                  onFocus={() => { if (eventList.length > 0) { setShowEventDropdown(true); setEventSearch(''); } }}
                  placeholder={eventList.length > 0 ? "Rechercher un événement..." : "Nom de l'événement"}
                  className="bg-background border-border/50 pl-10"
                />
              </div>
              {showEventDropdown && filteredEvents.length > 0 && (
                <div className="absolute z-50 w-full mt-1 bg-zinc-900 border border-zinc-700 rounded-lg shadow-xl max-h-60 overflow-auto">
                  {filteredEvents.map((event, idx) => (
                    <button
                      key={idx}
                      type="button"
                      onClick={() => selectEvent(event)}
                      className="w-full text-left px-3 py-2 text-sm text-zinc-200 hover:bg-[#C4A052]/20 hover:text-[#C4A052] transition-colors border-b border-zinc-800 last:border-0"
                    >
                      {event.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Date & Time */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Date événement</Label>
              <Input
                data-testid="date-evenement-input"
                type="date"
                value={formData.date_evenement}
                onChange={(e) => handleChange('date_evenement', e.target.value)}
                className="bg-background border-border/50"
              />
            </div>
            <div className="space-y-2">
              <Label>Heure</Label>
              <Input
                value={formData.heure_evenement}
                onChange={(e) => handleChange('heure_evenement', e.target.value)}
                placeholder="20:00"
                className="bg-background border-border/50"
              />
            </div>
          </div>

          {/* Location */}
          <div className="grid grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label>Lieu</Label>
              <Input
                value={formData.lieu}
                onChange={(e) => handleChange('lieu', e.target.value)}
                placeholder="Stade/Arena"
                className="bg-background border-border/50"
              />
            </div>
            <div className="space-y-2">
              <Label>Ville</Label>
              <Input
                value={formData.ville}
                onChange={(e) => handleChange('ville', e.target.value)}
                className="bg-background border-border/50"
              />
            </div>
            <div className="space-y-2">
              <Label>Pays</Label>
              <Input
                value={formData.pays}
                onChange={(e) => handleChange('pays', e.target.value)}
                className="bg-background border-border/50"
              />
            </div>
          </div>

          {/* Seating - adapté selon le projet */}
          {isRG ? (
            <>
              {/* Roland Garros: Catégorie, Tribune, Accès, Escalier, Rang, Place */}
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Catégorie</Label>
                  {showCustomCategory ? (
                    <div className="flex gap-2">
                      <Input
                        value={customCategory}
                        onChange={(e) => setCustomCategory(e.target.value.toUpperCase())}
                        placeholder="Nouvelle catégorie..."
                        className="bg-background border-border/50 flex-1"
                        autoFocus
                      />
                      <Button
                        type="button"
                        size="sm"
                        onClick={() => {
                          if (customCategory.trim()) {
                            handleChange('categorie', customCategory.trim());
                          }
                          setShowCustomCategory(false);
                          setCustomCategory('');
                        }}
                        className="bg-[#C4A052] hover:bg-[#C4A052]/80 text-black"
                      >
                        OK
                      </Button>
                    </div>
                  ) : (
                    <Select
                      value={formData.categorie}
                      onValueChange={(v) => {
                        if (v === '__NEW__') {
                          setShowCustomCategory(true);
                        } else {
                          handleChange('categorie', v);
                        }
                      }}
                    >
                      <SelectTrigger className="bg-background border-border/50">
                        <SelectValue placeholder="Sélectionner..." />
                      </SelectTrigger>
                      <SelectContent>
                        {availableCategories.map(cat => (
                          <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                        ))}
                        <SelectItem value="__NEW__" className="text-[#C4A052]">
                          <span className="flex items-center gap-1">
                            <Plus className="h-3 w-3" /> Nouvelle catégorie...
                          </span>
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  )}
                </div>
                <div className="space-y-2">
                  <Label>Tribune</Label>
                  <Input
                    value={formData.tribune}
                    onChange={(e) => handleChange('tribune', e.target.value)}
                    placeholder="Ex: Tribune A"
                    className="bg-background border-border/50"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Accès</Label>
                  <Input
                    value={formData.acces}
                    onChange={(e) => handleChange('acces', e.target.value)}
                    placeholder="Ex: Porte 5"
                    className="bg-background border-border/50"
                  />
                </div>
              </div>
              <div className="grid grid-cols-4 gap-4">
                <div className="space-y-2">
                  <Label>Escalier</Label>
                  <Input
                    value={formData.escalier}
                    onChange={(e) => handleChange('escalier', e.target.value)}
                    placeholder="Ex: E3"
                    className="bg-background border-border/50"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Rang</Label>
                  <Input
                    value={formData.rang}
                    onChange={(e) => handleChange('rang', e.target.value)}
                    className="bg-background border-border/50"
                  />
                </div>
                <div className="space-y-2">
                  <Label>{parseInt(formData.qty) > 1 ? 'Place début' : 'Place'}</Label>
                  <Input
                    value={formData.place}
                    onChange={(e) => handleChange('place', e.target.value)}
                    placeholder={parseInt(formData.qty) > 1 ? 'Ex: 1' : 'Ex: 5'}
                    className="bg-background border-border/50"
                  />
                </div>
                {parseInt(formData.qty) > 1 && (
                  <div className="space-y-2">
                    <Label>Place fin</Label>
                    <Input
                      value={formData.place_fin || ''}
                      onChange={(e) => handleChange('place_fin', e.target.value)}
                      placeholder="Ex: 9"
                      className="bg-background border-border/50"
                    />
                  </div>
                )}
              </div>
            </>
          ) : (
            <>
              {/* World Cup / Autre: Catégorie, Block, Rang, Place */}
              <div className="grid grid-cols-4 gap-4">
                <div className="space-y-2">
                  <Label>Catégorie</Label>
                  {showCustomCategory ? (
                    <div className="flex gap-2">
                      <Input
                        value={customCategory}
                        onChange={(e) => setCustomCategory(e.target.value.toUpperCase())}
                        placeholder="Nouvelle catégorie..."
                        className="bg-background border-border/50 flex-1"
                        autoFocus
                      />
                      <Button
                        type="button"
                        size="sm"
                        onClick={() => {
                          if (customCategory.trim()) {
                            handleChange('categorie', customCategory.trim());
                          }
                          setShowCustomCategory(false);
                          setCustomCategory('');
                        }}
                        className="bg-[#C4A052] hover:bg-[#C4A052]/80 text-black"
                      >
                        OK
                      </Button>
                    </div>
                  ) : (
                    <Select
                      value={formData.categorie}
                      onValueChange={(v) => {
                        if (v === '__NEW__') {
                          setShowCustomCategory(true);
                        } else {
                          handleChange('categorie', v);
                        }
                      }}
                    >
                      <SelectTrigger className="bg-background border-border/50">
                        <SelectValue placeholder="Sélectionner..." />
                      </SelectTrigger>
                      <SelectContent>
                        {availableCategories.map(cat => (
                          <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                        ))}
                        <SelectItem value="__NEW__" className="text-[#C4A052]">
                          <span className="flex items-center gap-1">
                            <Plus className="h-3 w-3" /> Nouvelle catégorie...
                          </span>
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  )}
                </div>
                <div className="space-y-2">
                  <Label>Block</Label>
                  <Input
                    value={formData.block}
                    onChange={(e) => handleChange('block', e.target.value)}
                    className="bg-background border-border/50"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Rang</Label>
                  <Input
                    value={formData.rang}
                    onChange={(e) => handleChange('rang', e.target.value)}
                    className="bg-background border-border/50"
                  />
                </div>
                <div className="space-y-2">
                  <Label>{parseInt(formData.qty) > 1 ? 'Place début' : 'Place'}</Label>
                  <Input
                    value={formData.place}
                    onChange={(e) => handleChange('place', e.target.value)}
                    placeholder={parseInt(formData.qty) > 1 ? 'Ex: 1' : 'Ex: 5'}
                    className="bg-background border-border/50"
                  />
                </div>
                {parseInt(formData.qty) > 1 && (
                  <div className="space-y-2">
                    <Label>Place fin</Label>
                    <Input
                      value={formData.place_fin || ''}
                      onChange={(e) => handleChange('place_fin', e.target.value)}
                      placeholder="Ex: 9"
                      className="bg-background border-border/50"
                    />
                  </div>
                )}
              </div>
            </>
          )}

          {/* Pricing */}
          <div className="grid grid-cols-4 gap-4">
            <div className="space-y-2">
              <Label>Quantité</Label>
              <Input
                data-testid="qty-input"
                type="number"
                min="1"
                value={formData.qty}
                onChange={(e) => handleChange('qty', parseInt(e.target.value) || 1)}
                className="bg-background border-border/50"
              />
            </div>
            <div className="space-y-2">
              <Label>Prix unitaire</Label>
              <Input
                data-testid="prix-input"
                type="number"
                step="0.01"
                value={formData.prix}
                onChange={(e) => handleChange('prix', parseFloat(e.target.value) || 0)}
                className="bg-background border-border/50"
              />
            </div>
            <div className="space-y-2">
              <Label>Devise</Label>
              <Select
                value={formData.devise}
                onValueChange={(v) => handleChange('devise', v)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="EUR">EUR €</SelectItem>
                  <SelectItem value="GBP">GBP £</SelectItem>
                  <SelectItem value="USD">USD $</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Total</Label>
              <Input
                type="number"
                step="0.01"
                value={formData.total}
                onChange={(e) => handleChange('total', parseFloat(e.target.value) || 0)}
                className="bg-background border-border/50"
              />
            </div>
          </div>

          {/* Client */}
          <div className="grid grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label>Nom client</Label>
              <Input
                value={formData.nom_client}
                onChange={(e) => handleChange('nom_client', e.target.value)}
                className="bg-background border-border/50"
              />
            </div>
            <div className="space-y-2">
              <Label>Email client <span className="text-zinc-500 text-xs">(optionnel)</span></Label>
              <Input
                type="text"
                value={formData.mail_client}
                onChange={(e) => handleChange('mail_client', e.target.value)}
                placeholder="email@exemple.com ou SANS EMAIL"
                className="bg-background border-border/50"
              />
            </div>
            <div className="space-y-2">
              <Label>Date de naissance <span className="text-zinc-500 text-xs">(optionnel)</span></Label>
              <Input
                type="text"
                value={formData.dob_client}
                onChange={(e) => handleChange('dob_client', e.target.value)}
                placeholder="JJ/MM/AAAA"
                className="bg-background border-border/50"
              />
            </div>
          </div>

          {/* Compte (pour ACHAT) */}
          {formData.type === 'ACHAT' && (
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>E-mail Compte</Label>
                <Input
                  data-testid="email-compte-input"
                  type="email"
                  value={formData.email_compte}
                  onChange={(e) => handleChange('email_compte', e.target.value)}
                  placeholder="compte@exemple.com"
                  className="bg-background border-border/50"
                />
              </div>
              <div className="space-y-2">
                <Label>PW Compte</Label>
                <Input
                  data-testid="pw-compte-input"
                  type="text"
                  value={formData.pw_compte}
                  onChange={(e) => handleChange('pw_compte', e.target.value)}
                  placeholder="Mot de passe du compte"
                  className="bg-background border-border/50"
                />
              </div>
            </div>
          )}

          {/* Source & Delivery */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Source</Label>
              {showCustomSource ? (
                <div className="flex gap-2">
                  <Input
                    value={customSource}
                    onChange={(e) => setCustomSource(e.target.value.toUpperCase())}
                    placeholder="Nouvelle source..."
                    className="bg-background border-border/50 flex-1"
                    autoFocus
                  />
                  <Button
                    type="button"
                    size="sm"
                    onClick={() => {
                      if (customSource.trim()) {
                        const src = customSource.trim().toUpperCase();
                        handleChange('source', src);
                        setAddedSources(prev => [...prev, src]);
                      }
                      setShowCustomSource(false);
                      setCustomSource('');
                    }}
                    className="bg-[#C4A052] hover:bg-[#C4A052]/80 text-black"
                  >
                    OK
                  </Button>
                </div>
              ) : (
                <Select
                  value={formData.source}
                  onValueChange={(v) => {
                    if (v === '__NEW__') {
                      setShowCustomSource(true);
                    } else {
                      handleChange('source', v);
                    }
                  }}
                >
                  <SelectTrigger className="bg-background border-border/50">
                    <SelectValue placeholder="Sélectionner..." />
                  </SelectTrigger>
                  <SelectContent>
                    {availableSources.map(src => (
                      <SelectItem key={src} value={src}>{src}</SelectItem>
                    ))}
                    <SelectItem value="__NEW__" className="text-[#C4A052]">
                      <span className="flex items-center gap-1">
                        <Plus className="h-3 w-3" /> Nouvelle source...
                      </span>
                    </SelectItem>
                  </SelectContent>
                </Select>
              )}
            </div>
            <div className="space-y-2">
              <Label>Mode livraison</Label>
              <Input
                value={formData.mode_livraison}
                onChange={(e) => handleChange('mode_livraison', e.target.value)}
                placeholder="E-TICKET, COURRIER..."
                className="bg-background border-border/50"
              />
            </div>
          </div>

          {/* Note */}
          <div className="space-y-2">
            <Label>Note</Label>
            <Textarea
              value={formData.note}
              onChange={(e) => handleChange('note', e.target.value)}
              className="bg-background border-border/50"
              rows={2}
            />
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t border-border/50">
            <Button
              type="button"
              variant="outline"
              onClick={() => { setPreviewLines(null); onOpenChange(false); }}
              className="border-border/50"
            >
              Annuler
            </Button>
            <Button
              type="submit"
              data-testid="save-ticket-button"
              disabled={loading || !formData.evenement}
              className="bg-primary hover:bg-primary/90"
            >
              {loading ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Save className="h-4 w-4 mr-2" />
              )}
              {ticket ? 'Enregistrer' : 'Aperçu'}
            </Button>
          </div>
        </form>

        {/* Preview - replaces form content */}
        {previewLines && (
          <div className="fixed inset-0 z-[100] bg-zinc-950/98 flex items-center justify-center p-4" onClick={() => setPreviewLines(null)}>
            <div className="bg-zinc-900 border border-[#C4A052]/30 rounded-xl max-w-2xl w-full max-h-[60vh] flex flex-col" onClick={e => e.stopPropagation()}>
              <div className="px-5 py-3 border-b border-zinc-800">
                <h3 className="text-base font-semibold text-[#C4A052]">
                  Aperçu — {previewLines.length} ligne{previewLines.length > 1 ? 's' : ''} à créer
                </h3>
              </div>
              <div className="overflow-y-auto flex-1 px-2 py-2">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-zinc-500 text-xs">
                      <th className="px-2 py-1 text-left w-8">#</th>
                      <th className="px-2 py-1 text-left">Type</th>
                      <th className="px-2 py-1 text-left">Événement</th>
                      <th className="px-2 py-1 text-left">Cat.</th>
                      <th className="px-2 py-1 text-left">Source</th>
                      <th className="px-2 py-1 text-left">Place</th>
                      <th className="px-2 py-1 text-left">Réf</th>
                    </tr>
                  </thead>
                  <tbody>
                    {previewLines.map((line) => (
                      <tr key={line._lineNum} className="border-t border-zinc-800/60">
                        <td className="px-2 py-1.5 text-zinc-500">{line._lineNum}</td>
                        <td className="px-2 py-1.5">
                          <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${line.type === 'ACHAT' ? 'bg-blue-500/20 text-blue-400' : 'bg-emerald-500/20 text-emerald-400'}`}>
                            {line.type}
                          </span>
                        </td>
                        <td className="px-2 py-1.5 text-white text-xs truncate max-w-[180px]" title={line.evenement}>{line.evenement}</td>
                        <td className="px-2 py-1.5 text-zinc-300 text-xs">{line.categorie}</td>
                        <td className="px-2 py-1.5 text-zinc-300 text-xs">{line.source || '-'}</td>
                        <td className="px-2 py-1.5 text-zinc-300 font-mono text-xs">{line.place || '-'}</td>
                        <td className="px-2 py-1.5 text-zinc-400 font-mono text-xs">{line.type === 'ACHAT' ? (line.ref_achat || '-') : (line.ref_vente || '-')}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="flex justify-end gap-3 px-5 py-3 border-t border-zinc-800">
                <Button variant="outline" onClick={() => setPreviewLines(null)} className="border-zinc-600">
                  Retour
                </Button>
                <Button
                  onClick={handleConfirmPreview}
                  disabled={loading}
                  className="bg-emerald-600 hover:bg-emerald-500 text-white"
                  data-testid="confirm-preview-btn"
                >
                  {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Check className="h-4 w-4 mr-2" />}
                  Valider ({previewLines.length} ligne{previewLines.length > 1 ? 's' : ''})
                </Button>
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default TicketFormModal;
